import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import Home from 'features/home/Home'
import Layout from 'components/layout/Layout'
import { routes } from 'utils/routes'
import { Language } from 'types/language'
import { DEFAULT_NAMESPACES, PRODUCERS_NAMESPACES } from 'constants/i18n'
import { useTranslation } from 'next-i18next/pages'
import type { CombinedCategories, HomeProps } from 'features/home/types'
import type { SeoProps } from 'components/seo/types'
import { cmsLib, producersLib } from 'lib'
import { sortCmsBlogArticlesOnPublishedDate } from 'utils/cms'
import { getLowerCaseLanguage } from 'utils/language'

const HomePage = (props: HomeProps) => {
  // i18n
  const { t } = useTranslation('home')

  // Constants
  const SEO: SeoProps = {
    description: t(($) => $.seo.description),
    title: t(($) => $.seo.title)
  }

  return (
    <Layout seo={SEO}>
      <Home {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ locale }) => {
  const language = getLowerCaseLanguage(locale)
  const [featuredProducers, encyclopediaArticles, blogArticles] = await Promise.all([
    producersLib.getFeaturedProducers({ language }),
    cmsLib.getEncyclopediaArticles(),
    cmsLib.getBlogArticles(language)
  ])

  const categories = encyclopediaArticles.reduce((prev: CombinedCategories, curr) => {
    if (!prev) {
      prev = {}
    }

    // if the category has no title or is not there, then skip the entry
    if (!curr.title || !curr.isPopular || !curr.category) {
      return prev
    }

    // Only allow a maximum of 5 entries per category
    if (prev[curr.category.title]?.articles?.length > 4) {
      return prev
    }

    const articleSlug = routes(Language.DUTCH, '', [curr.category.slug, curr.slug]).encyclopediaArticle

    // if the category is not in the return value yet, then add it
    if (!prev[curr.category.title]) {
      return {
        ...prev,
        [curr.category.title]: {
          slug: routes(Language.DUTCH, curr.category.slug).encyclopediaCategory,
          articles: [{ slug: articleSlug, title: curr.title }]
        }
      }
    }

    // add the article to the category object
    prev[curr.category.title].articles.push({ slug: articleSlug, title: curr.title })

    return prev
  }, {})

  return {
    props: {
      articles: sortCmsBlogArticlesOnPublishedDate(blogArticles, 4),
      categories,
      producers: featuredProducers.slice(0, 3),
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'home', 'simulation', ...PRODUCERS_NAMESPACES], i18nConfig))
    },
    revalidate: 600 // re-generate page when a request comes in, once every 10 minutes
  }
}

export default HomePage
